import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import {
  Casino,
  Check,
  Info,
} from '@mui/icons-material';
import { WinningPatternData } from '../../types';
import dayjs from 'dayjs';

interface WinningPatternDetailsModalProps {
  pattern: WinningPatternData | null;
  open: boolean;
  onClose: () => void;
}

const WinningPatternDetailsModal: React.FC<WinningPatternDetailsModalProps> = ({
  pattern,
  open,
  onClose,
}) => {
  if (!pattern) return null;

  const getPatternTypeColor = (patternType: string) => {
    switch (patternType) {
      case 'builtin': return 'primary';
      case 'custom': return 'secondary';
      default: return 'default';
    }
  };

  const getBuiltinTypeLabel = (builtinType?: string) => {
    switch (builtinType) {
      case 'standard': return 'Standard';
      case 'fourCorners': return 'Four Corners';
      case 'fullHouse': return 'Full House';
      case 'diamond': return 'Diamond';
      case 'xPattern': return 'X Pattern';
      default: return 'Unknown';
    }
  };

  const getBuiltinTypeDescription = (type: string) => {
    switch (type) {
      case 'standard': return 'Standard bingo - any row, column, or diagonal';
      case 'fourCorners': return 'Four corner squares must be marked';
      case 'fullHouse': return 'All squares on the card must be marked';
      case 'diamond': return 'Diamond pattern in the center';
      case 'xPattern': return 'X pattern across the card';
      default: return '';
    }
  };

  const renderCustomPattern = (pattern: number[][]) => {
    if (!pattern || pattern.length === 0) return 'No pattern';
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
        <Typography variant="subtitle2" gutterBottom>
          Custom Pattern Visualization
        </Typography>
        {pattern.map((row, rowIndex) => (
          <Box key={rowIndex} sx={{ display: 'flex', gap: 0.5 }}>
            {row.map((cell, colIndex) => (
              <Box
                key={colIndex}
                sx={{
                  width: 30,
                  height: 30,
                  backgroundColor: cell === 1 ? 'primary.main' : 'grey.200',
                  border: '1px solid',
                  borderColor: cell === 1 ? 'primary.dark' : 'grey.400',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {cell === 1 && <Check sx={{ color: 'white', fontSize: 16 }} />}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  const renderBuiltinPattern = (builtinType: string) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
        <Typography variant="subtitle2" gutterBottom>
          Built-in Pattern: {getBuiltinTypeLabel(builtinType)}
        </Typography>
        <Alert severity="info" sx={{ maxWidth: 300 }}>
          {getBuiltinTypeDescription(builtinType)}
        </Alert>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <Casino />
          <Typography variant="h6">Winning Pattern Details</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Basic Information */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Basic Information
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Name
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {pattern.name}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Type
                    </Typography>
                    <Chip
                      label={pattern.patternType}
                      color={getPatternTypeColor(pattern.patternType) as any}
                      size="small"
                    />
                  </Box>
                  {pattern.patternType === 'builtin' && pattern.builtinType && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Built-in Type
                      </Typography>
                      <Chip
                        label={getBuiltinTypeLabel(pattern.builtinType)}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  )}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Description
                    </Typography>
                    <Typography variant="body2">
                      {pattern.description}
                    </Typography>
                  </Box>
                  {pattern.visualPattern && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Visual Description
                      </Typography>
                      <Typography variant="body2">
                        {pattern.visualPattern}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Status
                    </Typography>
                    <Chip
                      label={pattern.isActive ? 'Active' : 'Inactive'}
                      color={pattern.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  {pattern.createdBy && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Created By
                      </Typography>
                      <Typography variant="body2">
                        {pattern.createdBy?.username || 'Unknown'}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>

            {/* Pattern Visualization */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Pattern Visualization
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    {pattern.patternType === 'custom' && pattern.customPattern ? (
                      renderCustomPattern(pattern.customPattern)
                    ) : pattern.patternType === 'builtin' && pattern.builtinType ? (
                      renderBuiltinPattern(pattern.builtinType)
                    ) : (
                      <Alert severity="warning">
                        No pattern visualization available
                      </Alert>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Metadata */}
            <Box sx={{ width: '100%' }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Metadata
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Created
                        </Typography>
                        <Typography variant="body2">
                          {pattern.createdAt ? dayjs(pattern.createdAt).format('MMM DD, YYYY HH:mm') : 'Unknown'}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Last Updated
                        </Typography>
                        <Typography variant="body2">
                          {pattern.updatedAt ? dayjs(pattern.updatedAt).format('MMM DD, YYYY HH:mm') : 'Unknown'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        Pattern ID
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {pattern.id || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WinningPatternDetailsModal; 